// ── company-people-section: who belongs to this company (QA #3) ─────────────
// Three panels, each owning its own honest-state read (same posture rules as the
// linked Programs / Cohorts panels):
//  - Participants: participants.list filtered to this company. The API scopes it
//    per role (staff all, a coach their cohorts, a company admin their company).
//  - Coaches: display names from this company's cohorts. It is the same read and
//    query key as the Cohorts panel, so one fetch. Names only, never emails (the
//    company-admin contract).
//  - Company admins: users.list role=company_admin for this company. The users
//    API is staff-only, so the panel renders only for roles that may open /users.

import { Badge, Module, ModuleBody, ModuleHead, humanizeLabel } from "@vjal/ui";
import { Link, useLocation } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { buildRolePath } from "../../components/RouteActionButton";
import { evaluateRouteAccess } from "../../domain/capabilities";
import type { CoachingRole } from "../../domain/roles";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { getCoachingServices } from "../../services/coaching-service-provider";
import type { CoachingCohortDto, CoachingParticipantDto, CoachingUserDto } from "../../services/coaching-dtos";
import { participantStatusLabel, participantStatusTone } from "../participants/participants-helpers";

const services = getCoachingServices();
/** One page at the API maximum; the panel says so when the company has more. */
const PARTICIPANT_PAGE_SIZE = 100;

export function CompanyPeopleSection({ companyId, role }: { companyId: string; role: CoachingRole }) {
  const showAdmins = evaluateRouteAccess(role, "/users").allowed;
  return (
    <div className="mt-4 grid gap-4 md:grid-cols-2" data-testid="company-people">
      <CompanyParticipantsPanel companyId={companyId} role={role} />
      <CompanyCoachesPanel companyId={companyId} />
      {showAdmins ? <CompanyAdminsPanel companyId={companyId} role={role} /> : null}
    </div>
  );
}

function PersonRow({
  name,
  href,
  detail,
  badge
}: {
  name: string;
  href?: string;
  detail?: string;
  badge?: { label: string; variant: ReturnType<typeof participantStatusTone> };
}) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-line bg-white px-3 py-2">
      <span className="flex min-w-0 flex-col">
        {href ? (
          <Link to={href} className="truncate text-sm font-medium text-link no-underline hover:underline">
            {name}
          </Link>
        ) : (
          <span className="truncate text-sm font-medium text-strong">{name}</span>
        )}
        {detail ? <span className="truncate text-xs text-ink-secondary">{detail}</span> : null}
      </span>
      {badge ? <Badge variant={badge.variant}>{badge.label}</Badge> : null}
    </li>
  );
}

function CompanyParticipantsPanel({ companyId, role }: { companyId: string; role: CoachingRole }) {
  const location = useLocation();
  const listQuery = { company_id: companyId, page: 1, pageSize: PARTICIPANT_PAGE_SIZE };
  const query = useHonestQuery(
    coachingQueryKeys.list("participants.list", listQuery),
    () => services.participants.list(listQuery),
    { isEmpty: (data) => participantRowsFor(data.items).length === 0 }
  );
  const rows = participantRowsFor(query.data?.items ?? []);
  const total = Math.max(query.data?.total ?? 0, rows.length);

  return (
    <Module span={6}>
      <ModuleHead title="Participants" subtitle="Participants enrolled with this company." />
      <ModuleBody>
        {query.posture !== null ? (
          <HonestState
            posture={query.posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={query.posture === "empty" ? "No participants yet" : undefined}
            message={query.posture === "empty" ? "Participants added to this company's cohorts will appear here." : undefined}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <ul className="m-0 flex flex-col gap-2 p-0" aria-label="Company participants">
              {rows.map((participant) => {
                const path = `/participants/${encodeURIComponent(participant.id)}`;
                return (
                  <PersonRow
                    key={participant.id}
                    name={participant.name || participant.email || `Participant ${participant.id}`}
                    href={evaluateRouteAccess(role, path).allowed ? buildRolePath(path, role, location.search) : undefined}
                    detail={[participant.email, participant.cohort_name].filter(Boolean).join(" · ")}
                    badge={{
                      label: participantStatusLabel(participant.status),
                      variant: participantStatusTone(participant.status)
                    }}
                  />
                );
              })}
            </ul>
            {total > rows.length ? (
              <p className="m-0 text-sm text-ink-secondary">{`Showing the first ${rows.length} of ${total} participants.`}</p>
            ) : null}
          </div>
        )}
      </ModuleBody>
    </Module>
  );
}

function CompanyCoachesPanel({ companyId }: { companyId: string }) {
  const query = useHonestQuery(
    coachingQueryKeys.list("cohorts.list", { company_id: companyId }),
    () => services.cohorts.list({ company_id: companyId }),
    { isEmpty: (data) => coachesFor(companyId, data.items).length === 0 }
  );
  const coaches = coachesFor(companyId, query.data?.items ?? []);

  return (
    <Module span={6}>
      <ModuleHead title="Coaches" subtitle="Coaches running this company's cohorts." />
      <ModuleBody>
        {query.posture !== null ? (
          <HonestState
            posture={query.posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={query.posture === "empty" ? "No coaches assigned yet" : undefined}
            message={query.posture === "empty" ? "Coaches assigned to this company's cohorts will appear here." : undefined}
          />
        ) : (
          <ul className="m-0 flex flex-col gap-2 p-0" aria-label="Company coaches">
            {coaches.map((coach) => (
              <PersonRow key={coach.key} name={coach.name} detail={coach.cohorts.join(", ")} />
            ))}
          </ul>
        )}
      </ModuleBody>
    </Module>
  );
}

function CompanyAdminsPanel({ companyId, role }: { companyId: string; role: CoachingRole }) {
  const location = useLocation();
  const listQuery = { company_id: companyId, role: "company_admin" };
  const query = useHonestQuery(
    coachingQueryKeys.list("users.list", listQuery),
    () => services.users.list(listQuery),
    { isEmpty: (data) => adminRowsFor(data.items).length === 0 }
  );
  const admins = adminRowsFor(query.data?.items ?? []);

  return (
    <Module span={6}>
      <ModuleHead title="Company admins" subtitle="People who manage this company's records." />
      <ModuleBody>
        {query.posture !== null ? (
          <HonestState
            posture={query.posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={query.posture === "empty" ? "No company admins assigned yet" : undefined}
            message={query.posture === "empty" ? "Assign a company admin to this company in Users > Edit." : undefined}
          />
        ) : (
          <ul className="m-0 flex flex-col gap-2 p-0" aria-label="Company admins">
            {admins.map((admin) => (
              <PersonRow
                key={admin.id}
                name={admin.name || admin.email || `User ${admin.id}`}
                href={buildRolePath(`/users/${encodeURIComponent(admin.id)}`, role, location.search)}
                detail={admin.email}
                badge={{
                  label: humanizeLabel(admin.status ?? "active"),
                  variant: (admin.status ?? "active") === "active" ? "success" : "default"
                }}
              />
            ))}
          </ul>
        )}
      </ModuleBody>
    </Module>
  );
}

function participantRowsFor(participants: CoachingParticipantDto[]): CoachingParticipantDto[] {
  return participants.filter((participant) => Boolean(participant.id));
}

function adminRowsFor(users: CoachingUserDto[]): CoachingUserDto[] {
  // The API's company filter also matches users with all-company YPO access; keep company admins only.
  return users.filter((user) => user.role === "company_admin");
}

/** One entry per coach across this company's cohorts, with the cohorts they run. */
export function coachesFor(companyId: string, cohorts: CoachingCohortDto[]) {
  const byCoach = new Map<string, { key: string; name: string; cohorts: string[] }>();
  for (const cohort of cohorts) {
    if (cohort.company_id != null && cohort.company_id !== companyId) continue;
    const name = cohort.coach_name?.trim();
    if (!name) continue;
    const key = cohort.coach_id ?? name;
    const entry = byCoach.get(key) ?? { key, name, cohorts: [] };
    entry.cohorts.push(cohort.name);
    byCoach.set(key, entry);
  }
  return [...byCoach.values()].sort((a, b) => a.name.localeCompare(b.name));
}
